// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile
} from 'firebase/auth';
import { ref, get, set, serverTimestamp } from 'firebase/database';
import { auth, database } from '../services/firebase';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    async function signup(email, password, displayName, phoneNumber) {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            // Mettre à jour le profil utilisateur avec le nom d'affichage
            await updateProfile(userCredential.user, { displayName });

            // Créer le profil utilisateur dans la base de données
            const userRef = ref(database, `users/${userCredential.user.uid}`);
            await set(userRef, {
                profile: {
                    displayName,
                    email,
                    phoneNumber,
                    photoURL: null,
                    role: "player", // Par défaut, tous les nouveaux utilisateurs sont des joueurs
                    createdAt: serverTimestamp(),
                    lastLogin: serverTimestamp()
                },
                gameHistory: {
                    totalGames: 0,
                    totalScore: 0,
                    lastPlayed: null
                },
                trainingStats: {
                    questionsAnswered: 0,
                    correctAnswers: 0,
                    categoryPerformance: {},
                    bookPerformance: {}
                },
                recentQuestions: []
            });

            return userCredential.user;
        } catch (error) {
            throw error;
        }
    }

    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return signOut(auth);
    }

    async function getUserRole(uid) {
        try {
            const userRef = ref(database, `users/${uid}/profile`);
            const snapshot = await get(userRef);

            if (snapshot.exists()) {
                const userData = snapshot.val();
                setUserRole(userData.role);

                // Mettre à jour le dernier login
                await set(ref(database, `users/${uid}/profile/lastLogin`), serverTimestamp());

                return userData.role;
            }
            return null;
        } catch (error) {
            console.error("Erreur lors de la récupération du rôle utilisateur:", error);
            return null;
        }
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                await getUserRole(user.uid);
            } else {
                setUserRole(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userRole,
        signup,
        login,
        logout,
        getUserRole
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
